import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import UpDownArrow from '../assets/ic_updown.svg';
import { useNavigate } from "react-router-dom";
import config from "../config";

const Challenges = () => {
  const navigate = useNavigate();

  const [affirmationList, setAffirmationList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const catId = 4; // Category ID for Challenges

  const fetchAffirmations = async () => {
    try {
      const response = await axios.get(`${config.BASE_URL}/apis/catId/admi-posts`, {
        params: { catId, page: currentPage },
      });

      if (response.data.success) {
        setAffirmationList(response.data.data.posts);
        setTotalPages(response.data.data.totalPages);
      }
    } catch (err) {
      console.error("Fetch error:", err);
    }
  };

  useEffect(() => {
    fetchAffirmations();
  }, [currentPage]);

  const handleDelete = async (id) => {
    const confirmed = window.confirm("Are you sure you want to delete this affirmation?");
    if (!confirmed) return;

    try {
      await axios.delete(`${config.BASE_URL}/apis/delete/postId-admin`, {
        params: { postId: id },
      });

      // Refetch data
      if (affirmationList.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchAffirmations();
      }
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = null;
      key = null;
    }
    setSortConfig({ key, direction });
  };

  const filteredAffirmations = useMemo(() => {
    if (!searchTerm) return affirmationList;
    const lowerSearch = searchTerm.toLowerCase();
    return affirmationList.filter(
      (item) =>
        item.title.toLowerCase().includes(lowerSearch)
    );
  }, [searchTerm, affirmationList]);

  const sortedAffirmations = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredAffirmations;
    const sorted = [...filteredAffirmations].sort((a, b) => {
      let aVal = a[sortConfig.key].toLowerCase();
      let bVal = b[sortConfig.key].toLowerCase();
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredAffirmations, sortConfig]);

  const totalEntries = sortedAffirmations.length;
  const paginatedAffirmations = useMemo(() => {
    const start = (currentPage - 1) * entriesPerPage;
    return sortedAffirmations.slice(start, start + entriesPerPage);
  }, [sortedAffirmations, currentPage, entriesPerPage]);

  const goToPage = (page) => {
    if (page < 1) page = 1;
    else if (page > totalPages) page = totalPages;
    setCurrentPage(page);
  };

  const headers = [
    { label: "Challenges Title", key: "title" },
    { label: "Challenge Message", key: "description" },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen font-sans">
      <div className="mb-4 flex flex-wrap justify-between items-center gap-3 bg-white rounded-[6px] px-4 py-3">
        <button
          type="button"
          onClick={() => navigate("/dashboard/add-challenge")}
          className="bg-[#FE7B38] text-white px-4 py-2 rounded hover:bg-[#D54E08] transition"
        >
          Add New Challenge +
        </button>

        <input
          type="search"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="overflow-x-auto p-3 bg-white rounded-[6px] border border-transparent">
        <table className="w-full text-left text-sm text-gray-700 table-fixed border border-gray-200">
          <thead className="bg-white border-b border-gray-300">
            <tr className="divide-x divide-gray-300">
              {headers.map(({ label, key }) => (
                <th key={key} className="p-4 select-none">
                  <div className="inline-flex items-center whitespace-nowrap space-x-2">
                    <span>{label}</span>
                    <img
                      src={UpDownArrow}
                      alt="Sort"
                      className={`w-4 h-4 cursor-pointer transition-transform ${sortConfig.key === key
                          ? sortConfig.direction === "asc"
                            ? "rotate-180"
                            : "rotate-0"
                          : ""
                        }`}
                      onClick={() => handleSort(key)}
                    />
                  </div>
                </th>
              ))}
              <th className="p-4 text-center">Action</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {paginatedAffirmations.length === 0 ? (
              <tr>
                <td colSpan={3} className="p-4 text-center text-gray-500">
                  No affirmations found.
                </td>
              </tr>
            ) : (
              paginatedAffirmations.map((item, index) => (
                <tr
                  key={item.id}
                  className={`divide-x divide-gray-300 ${index % 2 === 0 ? "bg-gray-100" : "bg-white"}`}
                >
                  <td className="p-4 max-w-xs break-words whitespace-normal text-gray-900 font-semibold line-clamp-3">
                    {item.title}
                  </td>
                  <td className="p-4 max-w-xs break-words whitespace-normal text-gray-700">
                    {item.description}
                  </td>
                  <td className="p-4 text-center">
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition"
                      aria-label={`Delete affirmation ${item.title}`}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="mt-4 flex flex-col md:flex-row items-center justify-between text-gray-600 text-sm space-y-2 md:space-y-0">
          <div className="text-black font-bold">
            Showing {totalEntries === 0 ? 0 : (currentPage - 1) * entriesPerPage + 1} to {Math.min(currentPage * entriesPerPage, totalEntries)} of {totalEntries} entries
          </div>

          <div className="flex items-center space-x-1">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-50"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (page) =>
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 2 && page <= currentPage + 2)
              )
              .map((page, i, arr) => {
                if (i > 0 && page - arr[i - 1] > 1) {
                  return (
                    <React.Fragment key={`gap-${page}`}>
                      <span className="px-3 py-1 text-gray-500 select-none">...</span>
                      <button
                        onClick={() => goToPage(page)}
                        className={`px-3 py-1 rounded ${page === currentPage ? "bg-orange-500 text-white font-semibold" : "hover:bg-gray-200"}`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                }
                return (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`px-3 py-1 rounded ${page === currentPage ? "bg-orange-500 text-white font-semibold" : "hover:bg-gray-200"}`}
                  >
                    {page}
                  </button>
                );
              })}

            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-50"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Challenges;
